import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Alert,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	OutlinedInput,
	Stack,
	Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { z as zod } from "zod";

import type { PriceBookItem, PriceBookPayload } from "@/hooks/api/use-campaign-requests";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";

import { marketLabel, PRICE_CODE_LABELS, PRICE_CODE_UNIT_HINTS } from "../constants";
import { labelOf } from "../humanise";
import { CONFIRMED_NOTE, isPlaceholderRate, isUnagreedRate, PLACEHOLDER_PREFIX } from "./rate-rows";
import { rateSchemaShape } from "./rate-schema";

const schema = zod.object({
	// The shared field rather than a second copy of the same rule: two amount schemas is how one
	// of them drifts from the DTO.
	amount: rateSchemaShape.amount,
	note: zod
		.string()
		.max(255, "255 characters at most")
		// Both markers, not just the one the server blocks on: a note that still says nobody agreed
		// this rate has not confirmed anything, whichever wording it uses.
		.refine((note) => !isUnagreedRate(note), {
			message: `A note starting "${PLACEHOLDER_PREFIX}" — or saying the rate is not agreed — leaves it unconfirmed. Say what was agreed instead`,
		}),
});

type FormValues = zod.infer<typeof schema>;

export interface ConfirmRateDialogProps {
	open: boolean;
	item: PriceBookItem | null;
	/** Whose rate is being confirmed, in words. */
	scopeLabel: string;
	isSaving: boolean;
	onClose: () => void;
	onConfirm: (payload: PriceBookPayload) => void;
}

/**
 * Confirming a placeholder, with the number in front of you.
 *
 * The amount is editable here on purpose. A seeded rate is a working number, not a price — the
 * whole point of the block is that nobody agreed it — so a one-click "confirm" that kept the seed
 * amount would launder a guess into a price. Confirming asks the operator to look at the figure
 * they are about to make quotable.
 */
export function ConfirmRateDialog({
	open,
	item,
	scopeLabel,
	isSaving,
	onClose,
	onConfirm,
}: ConfirmRateDialogProps): React.JSX.Element {
	const { control, handleSubmit, reset } = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: { amount: item?.amount ?? "", note: CONFIRMED_NOTE },
	});
	// `FormControl` does not wire a label to its input the way `TextField` does, so without these
	// a screen reader announces each field as a bare "edit text".
	const fieldId = React.useId();

	React.useLayoutEffect(() => {
		reset({ amount: item?.amount ?? "", note: CONFIRMED_NOTE });
	}, [item, reset, open]);

	const submit = handleSubmit((values) => {
		if (!item) return;

		const note = values.note.trim();

		onConfirm({
			code: item.code,
			accountId: item.accountId,
			market: item.market,
			// A decimal string, unchanged by anything on its way here.
			amount: values.amount.trim(),
			unit: item.unit,
			currency: item.currency,
			note: note.length > 0 ? note : null,
		});
	});

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Confirm this rate</DialogTitle>
			<DialogContent dividers>
				<Stack spacing={2}>
					<Alert severity="warning">
						<Typography variant="body2">
							<strong>{item ? labelOf(PRICE_CODE_LABELS, item.code) : "This rate"}</strong>
							{item?.market ? ` · ${marketLabel(item.market)}` : ""} is a working number for{" "}
							<strong>{scopeLabel}</strong> — its own note says nobody has agreed it.
						</Typography>
						<Typography variant="body2" sx={{ mt: 1 }}>
							{isPlaceholderRate(item?.note)
								? "Nothing can be quoted against it, so every request that needs this charge is sitting unpriced until it is answered."
								: "This wording does not stop it being quoted, so a request that needs this charge is going out at this figure right now. That is the reason to answer it first."}
						</Typography>
					</Alert>

					<Typography variant="body2" color="text.secondary">
						{item ? PRICE_CODE_UNIT_HINTS[item.code] : ""}
					</Typography>

					<Controller
						control={control}
						name="amount"
						render={({ field, fieldState }) => (
							<FormControl error={Boolean(fieldState.error)} fullWidth required>
								<InputLabel htmlFor={`${fieldId}-amount`}>Agreed amount ({item?.currency ?? "SGD"})</InputLabel>
								<OutlinedInput
									{...field}
									id={`${fieldId}-amount`}
									label={`Agreed amount (${item?.currency ?? "SGD"})`}
									placeholder="1200.0000"
									inputProps={{ inputMode: "decimal" }}
								/>
								{fieldState.error ? (
									<CustomFormHelperError text={fieldState.error.message || ""} />
								) : (
									<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
										The figure sitting there is a working number. Correct it here if the agreed price differs.
									</Typography>
								)}
							</FormControl>
						)}
					/>

					<Controller
						control={control}
						name="note"
						render={({ field, fieldState }) => (
							<FormControl error={Boolean(fieldState.error)} fullWidth>
								<InputLabel htmlFor={`${fieldId}-note`}>Note</InputLabel>
								<OutlinedInput {...field} id={`${fieldId}-note`} label="Note" />
								{fieldState.error ? (
									<CustomFormHelperError text={fieldState.error.message || ""} />
								) : (
									<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
										Replaces the placeholder note and releases the rate for quoting. Never shown to a client.
									</Typography>
								)}
							</FormControl>
						)}
					/>

					<Alert severity="info">
						<Typography variant="body2">
							Nothing already confirmed changes. A quote copies its rates onto its own lines when it is priced, so this
							applies to future quotes only.
						</Typography>
					</Alert>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={onClose} disabled={isSaving}>
					Cancel
				</Button>
				<Button variant="contained" color="warning" onClick={submit} disabled={isSaving || !item}>
					Confirm Rate
				</Button>
			</DialogActions>
		</Dialog>
	);
}
